// ABOUTME: Admin audit log interface component providing comprehensive audit trail viewing and management
// ABOUTME: Features filtering, pagination, export capabilities, and detailed audit log display for compliance

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack,
  FileDownload,
  PictureAsPdf,
  ExpandMore,
  ClearAll,
  Refresh,
  ContentCopy
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
  SortingState,
  ColumnFiltersState,
  getPaginationRowModel
} from '@tanstack/react-table';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { getAdminAuditLogs, exportAdminAuditLogs, getAdminAuditLogDetails } from '../services/api';
import { useTimezone } from '../contexts/TimezoneContext';

interface AdminAuditLogEntry {
  id: number;
  entityType: string;
  entityId: string;
  action: string;
  performedBy: number;
  performedByUserName: string;
  performedAt: string;
  details: string | null;
}

interface AdminAuditLogResponse {
  items: AdminAuditLogEntry[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

// Enhanced audit detail interfaces for Issue #505
interface AuditFieldChange {
  fieldName: string;
  oldValue?: string;
  newValue?: string;
  oldDisplayValue?: string;
  newDisplayValue?: string;
}

interface AdminAuditLogDetail {
  id: number;
  entityType: string;
  entityId: string;
  action: string;
  performedBy: number;
  performedByUserName: string;
  performedByUserRole?: string;
  performedAt: string;
  details?: string;
  oldValues?: string;
  newValues?: string;
  entityDisplayName?: string;
  familyName?: string;
  familyTimezone?: string;
  fieldChanges?: AuditFieldChange[];
}

const AdminAuditLog: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { getTodayInFamilyTimezone } = useTimezone();
  
  const [data, setData] = useState<AdminAuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [performedByFilter, setPerformedByFilter] = useState('');
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);

  // UI state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<AdminAuditLogEntry | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Enhanced modal state for Issue #505
  const [auditDetailLoading, setAuditDetailLoading] = useState(false);
  const [auditDetailError, setAuditDetailError] = useState<string | null>(null);
  const [auditDetail, setAuditDetail] = useState<AdminAuditLogDetail | null>(null);
  const [expandedJsonSection, setExpandedJsonSection] = useState<'old' | 'new' | null>(null);

  const columnHelper = createColumnHelper<AdminAuditLogEntry>();

  // Copy to clipboard utility
  const handleCopyToClipboard = useCallback(async (text: string, label: string = 'content') => {
    try {
      await navigator.clipboard.writeText(text);
      // TODO: Show success snackbar
      console.log(`${label} copied to clipboard`);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }, []);

  // Format JSON for display
  const formatJsonForDisplay = useCallback((jsonString: string | undefined | null) => {
    if (!jsonString) return '';
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch {
      return jsonString;
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, pageIndex: 0 })); // Reset to first page
      loadData();
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadData intentionally omitted; it is stable (useCallback) and adding it would conflict with the debounce logic
  }, [searchText, entityTypeFilter, actionFilter, performedByFilter, fromDate, toDate]);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const params = {
        page: pagination.pageIndex + 1, // API expects 1-based page
        pageSize: pagination.pageSize,
        ...(searchText && { search: searchText }),
        ...(entityTypeFilter && { entityType: entityTypeFilter }),
        ...(actionFilter && { action: actionFilter }),
        ...(performedByFilter && { performedBy: parseInt(performedByFilter) }),
        ...(fromDate && { fromDate: fromDate.toISOString() }),
        ...(toDate && { toDate: toDate.toISOString() }),
      };

      const result: AdminAuditLogResponse = await getAdminAuditLogs(params);
      setData(result.items);
      setTotalCount(result.totalCount);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [pagination, searchText, entityTypeFilter, actionFilter, performedByFilter, fromDate, toDate]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadData intentionally omitted; it is stable (useCallback) and including it would cause double-fetch when filters change
  }, [pagination]);

  const handleEntityIdClick = (entry: AdminAuditLogEntry) => {
    // Navigate to related entity based on entity type
    switch (entry.entityType.toLowerCase()) {
      case 'user':
        navigate(`/admin/users/${entry.entityId}`);
        break;
      case 'family':
        navigate(`/admin/families/${entry.entityId}`);
        break;
      case 'child':
        navigate(`/admin/children/${entry.entityId}`);
        break;
      default:
        // For system entities, just show details
        handleDetailsClick(entry);
    }
  };

  const handleDetailsClick = async (entry: AdminAuditLogEntry) => {
    setSelectedEntry(entry);
    setDetailsModalOpen(true);
    setAuditDetailLoading(true);
    setAuditDetailError(null);
    setAuditDetail(null);

    try {
      const detail = await getAdminAuditLogDetails(entry.id);
      setAuditDetail(detail);
    } catch (err) {
      console.error('Failed to load audit details:', err);
      setAuditDetailError('Failed to load detailed audit information');
    } finally {
      setAuditDetailLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'pdf') => {
    try {
      setExportLoading(true);
      
      const params = {
        format,
        ...(searchText && { search: searchText }),
        ...(entityTypeFilter && { entityType: entityTypeFilter }),
        ...(actionFilter && { action: actionFilter }),
        ...(performedByFilter && { performedBy: parseInt(performedByFilter) }),
        ...(fromDate && { fromDate: fromDate.toISOString() }),
        ...(toDate && { toDate: toDate.toISOString() }),
      };

      const blob = await exportAdminAuditLogs(params);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-log-${getTodayInFamilyTimezone()}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`Failed to export ${format}:`, err);
      setError(`Export failed: ${err}`);
    } finally {
      setExportLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchText('');
    setEntityTypeFilter('');
    setActionFilter('');
    setPerformedByFilter('');
    setFromDate(null);
    setToDate(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const truncateDetails = (details: string | null, maxLength: number = 50) => {
    if (!details) return '';
    return details.length > maxLength ? `${details.substring(0, maxLength)}...` : details;
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('performedAt', {
        header: 'Timestamp',
        cell: (info) => (
          <Typography variant="body2">
            {formatDate(info.getValue())}
          </Typography>
        ),
      }),
      columnHelper.accessor('entityType', {
        header: 'Entity Type',
        cell: (info) => (
          <Chip 
            label={info.getValue()} 
            size="small"
            color="primary"
            variant="outlined"
          />
        ),
      }),
      columnHelper.accessor('entityId', {
        header: 'Entity ID',
        cell: (info) => {
          const entry = info.row.original;
          const isClickable = ['user', 'family', 'child'].includes(entry.entityType.toLowerCase());
          
          return isClickable ? (
            <Button
              variant="text"
              size="small"
              onClick={() => handleEntityIdClick(entry)}
              sx={{ minWidth: 'auto', p: 0, textTransform: 'none' }}
            >
              {info.getValue()}
            </Button>
          ) : (
            <Typography variant="body2">{info.getValue()}</Typography>
          );
        },
      }),
      columnHelper.accessor('action', {
        header: 'Action',
        cell: (info) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {info.getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor('performedByUserName', {
        header: 'Performed By',
        cell: (info) => (
          <Typography variant="body2">
            {info.getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor('details', {
        header: 'Details',
        cell: (info) => {
          const details = info.getValue();
          const entry = info.row.original;
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {truncateDetails(details)}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleDetailsClick(entry)}
                data-testid="expand-details-button"
                title="View Details"
              >
                <ExpandMore />
              </IconButton>
            </Box>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- columnHelper and handleEntityIdClick are stable references created once per mount; adding them would recreate columns on every render
    []
  );

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    state: {
      sorting,
      columnFilters,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
  });

  if (isMobile) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin')}
            data-testid="back-to-admin-button"
          >
            Back to Admin
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              Audit Log
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Administrative actions and system events
            </Typography>
          </Box>
        </Box>

        <Box data-testid="audit-log-cards">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
              <Button onClick={loadData} size="small" sx={{ ml: 2 }}>
                Retry
              </Button>
            </Alert>
          ) : data.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No audit logs found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters
              </Typography>
            </Paper>
          ) : (
            data.map((entry) => (
              <Card key={entry.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6">
                      {entry.action}
                    </Typography>
                    <Chip label={entry.entityType} size="small" color="primary" />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {formatDate(entry.performedAt)} • {entry.performedByUserName}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Entity ID: {entry.entityId}
                  </Typography>
                  {entry.details && (
                    <Typography variant="body2" color="text.secondary">
                      {truncateDetails(entry.details, 100)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin')}
            data-testid="back-to-admin-button"
          >
            Back to Admin
          </Button>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h3" sx={{ fontWeight: 600, mb: 1 }}>
              Audit Log
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Administrative actions and system events
            </Typography>
          </Box>
          
          {/* Export buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={() => handleExport('csv')}
              disabled={exportLoading}
              data-testid="export-csv-button"
              size="small"
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdf />}
              onClick={() => handleExport('pdf')}
              disabled={exportLoading}
              data-testid="export-pdf-button"
              size="small"
            >
              Export PDF
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>Filters</Typography>
            <Button
              variant="outlined"
              startIcon={<ClearAll />}
              onClick={clearFilters}
              size="small"
              data-testid="clear-filters-button"
            >
              Clear Filters
            </Button>
            <IconButton onClick={loadData} size="small" data-testid="refresh-button">
              <Refresh />
            </IconButton>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
            <TextField
              label="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              data-testid="search-input"
              inputProps={{ 'aria-label': 'Search audit logs' }}
            />
            
            <FormControl size="small">
              <InputLabel>Entity Type</InputLabel>
              <Select
                value={entityTypeFilter}
                label="Entity Type"
                onChange={(e) => setEntityTypeFilter(e.target.value)}
                data-testid="entity-type-filter"
                inputProps={{ 'aria-label': 'Filter by entity type' }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Family">Family</MenuItem>
                <MenuItem value="Child">Child</MenuItem>
                <MenuItem value="Goal">Goal</MenuItem>
                <MenuItem value="Behavior">Behavior</MenuItem>
                <MenuItem value="System">System</MenuItem>
                <MenuItem value="Policy">Policy</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Action</InputLabel>
              <Select
                value={actionFilter}
                label="Action"
                onChange={(e) => setActionFilter(e.target.value)}
                data-testid="action-filter"
                inputProps={{ 'aria-label': 'Filter by action' }}
              >
                <MenuItem value="">All Actions</MenuItem>
                <MenuItem value="SoftDelete">SoftDelete</MenuItem>
                <MenuItem value="Restore">Restore</MenuItem>
                <MenuItem value="AdminSupport">AdminSupport</MenuItem>
                <MenuItem value="PasswordReset">PasswordReset</MenuItem>
                <MenuItem value="BackupCreated">BackupCreated</MenuItem>
                <MenuItem value="BackupFailed">BackupFailed</MenuItem>
                <MenuItem value="BackupDeleted">BackupDeleted</MenuItem>
                <MenuItem value="User Registration">User Registration</MenuItem>
                <MenuItem value="Family Created">Family Created</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
            <FormControl size="small">
              <InputLabel>Performed By</InputLabel>
              <Select
                value={performedByFilter}
                label="Performed By"
                onChange={(e) => setPerformedByFilter(e.target.value)}
                data-testid="performed-by-filter"
                inputProps={{ 'aria-label': 'Filter by admin user' }}
              >
                <MenuItem value="">All Admins</MenuItem>
                {/* TODO: Populate with actual admin users */}
              </Select>
            </FormControl>

            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue as Dayjs | null)}
              slotProps={{
                textField: {
                  size: 'small',
                  inputProps: { 'aria-label': 'From date' }
                }
              }}
              data-testid="from-date-input"
            />

            <DatePicker
              label="To Date"
              value={toDate}
              onChange={(newValue) => setToDate(newValue as Dayjs | null)}
              slotProps={{
                textField: {
                  size: 'small',
                  inputProps: { 'aria-label': 'To date' }
                }
              }}
              data-testid="to-date-input"
            />
          </Box>
        </Paper>

        {/* Table */}
        <Paper sx={{ overflow: 'hidden' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress data-testid="loading-indicator" />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
              <Button onClick={loadData} size="small" sx={{ ml: 2 }} data-testid="retry-button">
                Retry
              </Button>
            </Alert>
          )}

          {!loading && !error && data.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No audit logs found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters or check back later
              </Typography>
            </Box>
          )}

          {!loading && !error && data.length > 0 && (
            <>
              <Box sx={{ overflow: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }} data-testid="audit-log-table" tabIndex={0}>
                  <Box component="thead">
                    {table.getHeaderGroups().map(headerGroup => (
                      <Box component="tr" key={headerGroup.id} sx={{ backgroundColor: theme.palette.grey[50] }}>
                        {headerGroup.headers.map(header => (
                          <Box
                            component="th"
                            key={header.id}
                            sx={{
                              padding: '12px',
                              textAlign: 'left',
                              fontWeight: 600,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                  <Box component="tbody">
                    {table.getRowModel().rows.map(row => (
                      <Box component="tr" key={row.id} sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                        {row.getVisibleCells().map(cell => (
                          <Box
                            component="td"
                            key={cell.id}
                            sx={{
                              padding: '12px',
                              verticalAlign: 'top',
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Pagination */}
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} data-testid="pagination-controls">
                <Typography variant="body2" color="text.secondary">
                  Showing {pagination.pageIndex * pagination.pageSize + 1} to{' '}
                  {Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalCount)} of {totalCount} entries
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small">
                    <InputLabel>Rows per page</InputLabel>
                    <Select
                      value={pagination.pageSize}
                      label="Rows per page"
                      onChange={(e) => setPagination(prev => ({ ...prev, pageSize: Number(e.target.value), pageIndex: 0 }))}
                      inputProps={{ 'aria-label': 'Rows per page' }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPagination(prev => ({ ...prev, pageIndex: Math.max(0, prev.pageIndex - 1) }))}
                    disabled={!table.getCanPreviousPage()}
                    aria-label="Go to previous page"
                  >
                    Previous
                  </Button>
                  
                  <Typography variant="body2">
                    Page {pagination.pageIndex + 1} of {table.getPageCount()}
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPagination(prev => ({ ...prev, pageIndex: Math.min(table.getPageCount() - 1, prev.pageIndex + 1) }))}
                    disabled={!table.getCanNextPage()}
                    aria-label="Go to next page"
                    data-testid="next-page-button"
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Paper>

        {/* Enhanced Audit Details Modal - Issue #505 */}
        <Dialog
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          maxWidth="lg"
          fullWidth
          data-testid="audit-details-modal"
          aria-modal="true"
          aria-labelledby="audit-details-title"
        >
          <DialogTitle id="audit-details-title">
            <Typography variant="h5" component="h2">
              Audit Details
            </Typography>
            {auditDetail && (
              <Typography variant="subtitle1" color="text.secondary">
                {auditDetail.entityType} • {auditDetail.action} • {formatDate(auditDetail.performedAt)}
              </Typography>
            )}
          </DialogTitle>
          
          <DialogContent dividers sx={{ minHeight: '400px' }}>
            {auditDetailLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading detailed audit information...
                </Typography>
              </Box>
            )}

            {auditDetailError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {auditDetailError}
                <Button
                  size="small"
                  onClick={() => selectedEntry && handleDetailsClick(selectedEntry)}
                  sx={{ ml: 2 }}
                >
                  Retry
                </Button>
              </Alert>
            )}

            {auditDetail && !auditDetailLoading && (
              <Box data-testid="audit-details-content">
                {/* Header Information */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" gutterBottom>
                        Action Information
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Entity:</strong> {auditDetail.entityDisplayName || `${auditDetail.entityType} (ID: ${auditDetail.entityId})`}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Action:</strong> {auditDetail.action}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Performed By:</strong> {auditDetail.performedByUserName}
                        {auditDetail.performedByUserRole && ` (${auditDetail.performedByUserRole})`}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Date & Time:</strong> {formatDate(auditDetail.performedAt)}
                      </Typography>
                    </Paper>
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" gutterBottom>
                        Context Information
                      </Typography>
                      {auditDetail.familyName && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Family:</strong> {auditDetail.familyName}
                        </Typography>
                      )}
                      {auditDetail.familyTimezone && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Timezone:</strong> {auditDetail.familyTimezone}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        <strong>Entity Type:</strong> {auditDetail.entityType}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>

                {/* Field Changes - Side by Side Comparison */}
                {auditDetail.fieldChanges && auditDetail.fieldChanges.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      Field Comparison
                      <Tooltip title="Copy field changes summary">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const summary = auditDetail.fieldChanges!
                              .map(fc => `${fc.fieldName}: ${fc.oldDisplayValue || fc.oldValue} → ${fc.newDisplayValue || fc.newValue}`)
                              .join('\n');
                            handleCopyToClipboard(summary, 'Field changes summary');
                          }}
                          sx={{ ml: 1 }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                    
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Field</strong></TableCell>
                            <TableCell><strong>Old Value</strong></TableCell>
                            <TableCell><strong>New Value</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {auditDetail.fieldChanges.map((change, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Chip label={change.fieldName} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ p: 1, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1, maxWidth: '300px' }}>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                    {change.oldDisplayValue || change.oldValue || '(empty)'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ p: 1, bgcolor: 'success.light', color: 'success.contrastText', borderRadius: 1, maxWidth: '300px' }}>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                    {change.newDisplayValue || change.newValue || '(empty)'}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Enhanced Details Section */}
                {auditDetail.details && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      Details
                      <Tooltip title="Copy details">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyToClipboard(auditDetail.details!, 'Details')}
                          sx={{ ml: 1 }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: theme.palette.grey[50] }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {auditDetail.details}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* Raw JSON Data Section */}
                {(auditDetail.oldValues || auditDetail.newValues) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      Raw JSON Data
                      <Tooltip title="Copy complete audit data">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const fullData = {
                              action: auditDetail.action,
                              entity: `${auditDetail.entityType} (${auditDetail.entityId})`,
                              performedBy: auditDetail.performedByUserName,
                              performedAt: auditDetail.performedAt,
                              details: auditDetail.details,
                              oldValues: auditDetail.oldValues ? JSON.parse(auditDetail.oldValues) : null,
                              newValues: auditDetail.newValues ? JSON.parse(auditDetail.newValues) : null,
                            };
                            handleCopyToClipboard(JSON.stringify(fullData, null, 2), 'Complete audit data');
                          }}
                          sx={{ ml: 1 }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Typography>

                    {auditDetail.oldValues && (
                      <Accordion expanded={expandedJsonSection === 'old'} onChange={() => setExpandedJsonSection(expandedJsonSection === 'old' ? null : 'old')}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle1">Old Values</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ position: 'relative' }}>
                            {/* Monospace code block sizing paired with the (out-of-scope) fontFamily override, not a Typography variant */}
                            <Paper sx={{ p: 2, bgcolor: 'grey.100', fontFamily: 'monospace', fontSize: '0.875rem', overflow: 'auto', maxHeight: '300px' }}>
                              <pre>{formatJsonForDisplay(auditDetail.oldValues)}</pre>
                            </Paper>
                            <Tooltip title="Copy old values">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyToClipboard(formatJsonForDisplay(auditDetail.oldValues), 'Old values')}
                                sx={{ position: 'absolute', top: 8, right: 8 }}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {auditDetail.newValues && (
                      <Accordion expanded={expandedJsonSection === 'new'} onChange={() => setExpandedJsonSection(expandedJsonSection === 'new' ? null : 'new')}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle1">New Values</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ position: 'relative' }}>
                            {/* Monospace code block sizing paired with the (out-of-scope) fontFamily override, not a Typography variant */}
                            <Paper sx={{ p: 2, bgcolor: 'grey.100', fontFamily: 'monospace', fontSize: '0.875rem', overflow: 'auto', maxHeight: '300px' }}>
                              <pre>{formatJsonForDisplay(auditDetail.newValues)}</pre>
                            </Paper>
                            <Tooltip title="Copy new values">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyToClipboard(formatJsonForDisplay(auditDetail.newValues), 'New values')}
                                sx={{ position: 'absolute', top: 8, right: 8 }}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          
          <DialogActions>
            <Button 
              onClick={() => setDetailsModalOpen(false)} 
              data-testid="close-modal-button"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default AdminAuditLog;